import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { Form, Formik } from "formik";
import { useState } from "react";
import { FaSpinner } from "react-icons/fa";
import {
  createToken,
  CreateTokenRequest,
  CreateTokenResponse
} from "../../../api/services/tokens";
import { Permission } from "../../../api/services/rbac";
import { Button } from "../../../ui/Buttons/Button";
import { Modal } from "../../../ui/Modal";
import FormikTextInput from "../../Forms/Formik/FormikTextInput";
import FormikSelectDropdown from "../../Forms/Formik/FormikSelectDropdown";
import { toastSuccess } from "../../Toast/toast";
import {
  OBJECTS,
  getActionsForObject,
  getAllObjectActions
} from "../tokenUtils";
import TokenScopeFieldsGroup from "./TokenScopeFieldsGroup";
import FormikCheckbox from "@flanksource-ui/components/Forms/Formik/FormikCheckbox";
import { ErrorViewer } from "@flanksource-ui/components/ErrorViewer";

export type TokenFormValues = CreateTokenRequest & {
  objectActions: Record<string, boolean>;
};

type CreateTokenFormProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (
    response: CreateTokenResponse,
    formValues: TokenFormValues
  ) => void;
  isMcpSetup?: boolean;
};

type CreateTokenFormContentProps = {
  onSuccess?: (
    response: CreateTokenResponse,
    formValues: TokenFormValues
  ) => void;
  isMcpSetup?: boolean;
  showFooter?: boolean;
  formId?: string;
};

const expiryOptions = [
  { label: "30 days", value: "30d" },
  { label: "90 days", value: "90d" },
  { label: "180 days", value: "180d" },
  { label: "1 year", value: "365d" },
  { label: "Never", value: "" }
];

export function CreateTokenFormContent({
  onSuccess = () => {},
  isMcpSetup = false,
  showFooter = true,
  formId
}: CreateTokenFormContentProps) {
  const [submitError, setSubmitError] = useState<unknown>(null);

  const { mutate: createTokenMutation, isLoading } = useMutation({
    mutationFn: createToken,
    onSuccess: () => {
      setSubmitError(null);
      toastSuccess("Token created successfully");
    },
    onError: (error: unknown) => {
      setSubmitError(error);
    }
  });

  const allObjectActions = getAllObjectActions();

  const getInitialObjectActions = () => {
    const initialActions = Object.fromEntries(
      allObjectActions.map((objAction) => [objAction, false])
    );

    if (isMcpSetup) {
      // For MCP setup, pre-select mcp:* (will be shown in Custom mode)
      allObjectActions
        .filter((objAction) => objAction.startsWith("mcp:"))
        .forEach((mcpAction) => {
          initialActions[mcpAction] = true;
        });
    } else {
      // For regular token creation, apply "Read" preset by default
      // This matches the default selected scope in TokenScopeFieldsGroup
      OBJECTS.forEach((object) => {
        const actions = getActionsForObject(object);
        if (actions.includes("read")) {
          initialActions[`${object}:read`] = true;
        } else if (object === "mcp" && actions.includes("*")) {
          initialActions[`${object}:*`] = true;
        }
      });
    }

    return initialActions;
  };

  const handleFormSubmit = (values: TokenFormValues) => {
    const selectedScopes: Permission[] = Object.entries(values.objectActions)
      .filter(([_, isChecked]) => isChecked === true)
      .map(([scopeId]) => {
        // Split by first colon only to handle cases like "playbooks:playbook:run"
        const colonIndex = scopeId.indexOf(":");
        const object = scopeId.substring(0, colonIndex);
        const action = scopeId.substring(colonIndex + 1);
        return {
          subject: "", // subject is handled at server
          object,
          action
        } as Permission;
      });

    const tokenRequest: CreateTokenRequest = {
      name: values.name,
      expiry: values.expiry,
      autoRenew: values.autoRenew,
      scope: selectedScopes
    };

    setSubmitError(null);

    createTokenMutation(tokenRequest, {
      onSuccess: (data) => {
        onSuccess(data, values);
      }
    });
  };

  return (
    <Formik<TokenFormValues>
      initialValues={{
        name: "",
        expiry: "90d",
        autoRenew: false,
        objectActions: getInitialObjectActions()
      }}
      enableReinitialize
      onSubmit={handleFormSubmit}
      validate={(values) => {
        const errors: any = {};
        if (!values.name.trim()) {
          errors.name = "Name is required";
        }
        return errors;
      }}
    >
      {({ handleSubmit, isValid }) => (
        <Form
          id={formId}
          className="flex flex-1 flex-col"
          onSubmit={handleSubmit}
        >
          <div className={clsx("my-2 flex h-full flex-col")}>
            <div className={clsx("mb-2 flex flex-col px-2")}>
              <div className="flex flex-col space-y-4 p-4">
                <FormikTextInput
                  name="name"
                  label="Name"
                  required
                  hint="A descriptive name for this token"
                />
                <FormikSelectDropdown
                  name="expiry"
                  label="Expiry"
                  options={expiryOptions}
                  hint="When this token should expire"
                />
                <FormikCheckbox
                  name="autoRenew"
                  label="Auto Renew"
                  hint="If the token is being used continuously, it's expiry is renewed"
                  checkboxStyle="toggle"
                />

                <TokenScopeFieldsGroup isMcpSetup={isMcpSetup} />
              </div>
            </div>
          </div>
          {submitError != null && (
            <ErrorViewer error={submitError} className="mx-6 mb-4" />
          )}
          {showFooter && (
            <div
              className={clsx(
                "flex items-center justify-end bg-gray-100 px-5 py-4"
              )}
            >
              <Button
                icon={
                  isLoading ? <FaSpinner className="animate-spin" /> : undefined
                }
                type="submit"
                text={isLoading ? "Creating..." : "Create Token"}
                className="btn-primary"
                disabled={!isValid || isLoading}
              />
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
}

export default function CreateTokenForm({
  isOpen,
  onClose,
  onSuccess = () => {},
  isMcpSetup = false
}: CreateTokenFormProps) {
  return (
    <Modal
      title="Create new token"
      onClose={onClose}
      open={isOpen}
      bodyClass="flex flex-col w-full flex-1 h-full overflow-y-auto"
    >
      <CreateTokenFormContent onSuccess={onSuccess} isMcpSetup={isMcpSetup} />
    </Modal>
  );
}
